import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ClientPresentationDesignerV2_EstateWaterfallFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';
import {
  EstateWaterfallProjectionInput,
  EstateWaterfallWhereInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { applyWaterfallFilter } from '../EstateWaterfall.utils';
import {
  EstateWaterfall_EstateWaterfallVizFragment,
  EstateWaterfallFragment,
  useEstateWaterfallQuery,
  useEstateWaterfallVisualizationLazyQuery,
} from '../graphql/EstateWaterfall.generated';
import { getNodeId } from '../waterfallGraph/utils';

interface UseQueryWaterfallProps {
  waterfallId: string;
  householdId: string;
  /**
   * Optional projection overrides (death years / growth profile). When set — e.g.
   * editing a waterfall from a presentation slide — the viz is recomputed with the
   * slide's assumptions instead of the waterfall's own defaults. The base waterfall
   * (layout / node configs) still comes from the default query; only the viz is
   * overridden, keeping a single waterfall-level layout configuration.
   */
  projectionInput?: EstateWaterfallProjectionInput;
}

export function useQueryWaterfall({
  waterfallId,
  householdId,
  projectionInput,
}: UseQueryWaterfallProps) {
  const { primaryClients } = useHouseholdDetailsContext();
  const { showFeedback } = useFeedback();

  const {
    data,
    loading: isFetchingFromNetwork,
    refetch: _,
    ...rest
  } = useEstateWaterfallQuery({
    variables: {
      where: {
        id: waterfallId,
        hasHouseholdWith: [{ id: householdId }],
      },
    },
    fetchPolicy: 'cache-and-network',
    // This makes loading / isFetchingFromNetwork update in response to refetch queries
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      showFeedback(
        `Sorry, we failed to load the page. Please refresh and try again.`
      );
      diagnostics.error('estate waterfall load failure', error, {
        waterfallId,
      });
    },
  });

  const [
    fetchVisualization,
    {
      data: currentVisualizationData,
      previousData: previousVisualizationData,
      loading: isFetchingVisualization,
      error: visualizationError,
    },
  ] = useEstateWaterfallVisualizationLazyQuery({
    fetchPolicy: 'no-cache',
    onError: (error) => {
      showFeedback(FeedbackMessages.queryError);
      diagnostics.error('estate waterfall refresh failure', error, {
        waterfallId,
      });
    },
  });

  // Apollo clears `data` while the lazy query runs with new variables, for
  // example when the user toggles "Hide unfunded tiles". Keep the previous viz
  // until the new one arrives. If we do not, the edit trowser treats the refetch
  // as the initial projection load and remounts the editor with stale settings.
  const visualizationData =
    currentVisualizationData ?? previousVisualizationData;

  // Re-pull the viz through the lazy query, always re-applying the projection
  // input so overrides (death years / growth profile) survive every refetch —
  // e.g. toggling hide-unfunded-tiles must not drop the presentation's
  // assumptions.
  const fetchVisualizationWithOverrides = useCallback(
    (variables?: {
      where?: EstateWaterfallWhereInput;
      hideUnfundedTiles?: boolean;
    }) => {
      return fetchVisualization({
        variables: {
          where: {
            id: waterfallId,
            hasHouseholdWith: [{ id: householdId }],
            ...variables?.where,
          },
          hideUnfundedTiles: variables?.hideUnfundedTiles,
          input: projectionInput,
        },
      });
    },
    [fetchVisualization, waterfallId, householdId, projectionInput]
  );

  // Apply the projection override up front (before any user interaction) so the
  // editor renders the same assumptions the presentation slide showed. Gated on
  // `projectionInput` so the app edit flow (no override) is unaffected.
  useEffect(() => {
    if (!projectionInput) return;
    void fetchVisualizationWithOverrides();
  }, [projectionInput, fetchVisualizationWithOverrides]);

  const waterfall: EstateWaterfallFragment | null = useMemo(() => {
    const waterfall = getNodes(data?.estateWaterfalls)[0] ?? null;
    const visualizationWaterfall = getNodes(
      visualizationData?.estateWaterfalls
    )[0];

    const visualizationOverride: EstateWaterfall_EstateWaterfallVizFragment | null =
      visualizationWaterfall?.visualizationWithProjections ?? null;

    if (waterfall && visualizationOverride) {
      return {
        ...waterfall,
        visualizationWithProjections: visualizationOverride,
        // Mirror the projection's death years onto the waterfall so the timeline
        // section labels ("Before/After <name>'s death (YEAR)") reflect the
        // presentation's assumptions — waterfallGraph reads these off the
        // waterfall, not the viz. Fall back to the waterfall's own values when a
        // projection year is unset (e.g. the app edit flow passes no override).
        firstGrantorDeathYear:
          projectionInput?.firstGrantorDeathYear ??
          waterfall.firstGrantorDeathYear,
        secondGrantorDeathYear:
          projectionInput?.secondGrantorDeathYear ??
          waterfall.secondGrantorDeathYear,
      } as EstateWaterfallFragment;
    }

    return waterfall;
  }, [
    data?.estateWaterfalls,
    visualizationData?.estateWaterfalls,
    projectionInput,
  ]);

  const hasData = !!waterfall && !!primaryClients;
  const isRefetching =
    (isFetchingFromNetwork && !!waterfall) || isFetchingVisualization;

  // When a projection override is requested, the base query resolves first with
  // the waterfall's default viz; consumers should hold render until the override
  // viz arrives so the default assumptions never flash on screen. If the override
  // fetch errors, stop waiting and fall back to the base viz (the user already
  // gets error feedback) so the editor isn't permanently blocked.
  const isAwaitingProjectionOverride =
    !!projectionInput && !visualizationData && !visualizationError;

  const isInitialLoading = !hasData;
  const isWaterfallNotFound = !isFetchingFromNetwork && !waterfall;

  const refetch = useCallback(
    (variables: {
      where?: EstateWaterfallWhereInput;
      hideUnfundedTiles?: boolean;
    }) => {
      return fetchVisualizationWithOverrides(variables);
    },
    [fetchVisualizationWithOverrides]
  );

  const {
    waterfallWithFilter,
    isFilteredWaterfall,
    hasWaterfallData,
    visibleNodeIds,
    hiddenNodeIds,
    initWaterfallNodeIds,
  } = useEstateWaterfallDetails(waterfall);
  return {
    isInitialLoading,
    isUpdatingWaterfall: isRefetching,
    isAwaitingProjectionOverride,
    hasWaterfallData,
    isWaterfallNotFound,
    primaryClients,
    waterfall: waterfallWithFilter,
    loading: isFetchingFromNetwork,
    isFilteredWaterfall,
    visibleNodeIds,
    hiddenNodeIds,
    initWaterfallNodeIds,
    refetch,
    ...rest,
  };
}

export function useEstateWaterfallDetails<
  T extends
    | EstateWaterfallFragment
    | ClientPresentationDesignerV2_EstateWaterfallFragment,
>(waterfall: T | null): useEstateWaterfallDetailsOutput<T> {
  // The whole waterfall gets remounted when the waterfallId changes, so this resets to an empty set automatically
  const [initWaterfallNodeIds, setInitWaterfallNodeIds] = useState<Set<string>>(
    new Set<string>()
  );
  const waterfallWithFilter = useMemo(() => {
    return applyWaterfallFilter(waterfall);
  }, [waterfall]);

  const hasWaterfallData =
    waterfall?.visualizationWithProjections.hasWaterfallData ?? false;
  const isFilteredWaterfall =
    waterfallWithFilter?.visualizationWithProjections.nodes.length !==
    waterfall?.visualizationWithProjections.nodes.length;

  const visibleNodeIds = useMemo(
    () =>
      waterfallWithFilter?.visualizationWithProjections.nodes.map((node) =>
        getNodeId({
          id: node.id,
          afterDeath: node.afterDeath,
        })
      ) ?? [],
    [waterfallWithFilter?.visualizationWithProjections.nodes]
  );

  const hiddenNodeIds = useMemo(() => {
    return (
      waterfall?.visualizationWithProjections.nodes
        .filter(
          (node) =>
            !visibleNodeIds?.includes(
              getNodeId({
                id: node.id,
                afterDeath: node.afterDeath,
              })
            )
        )
        .map((node) =>
          getNodeId({
            id: node.id,
            afterDeath: node.afterDeath,
          })
        ) ?? []
    );
  }, [waterfall?.visualizationWithProjections.nodes, visibleNodeIds]);

  useEffect(() => {
    if (initWaterfallNodeIds.size > 0) return;
    if (!waterfall) return;

    const waterfallIds = new Set<string>();
    waterfall.visualizationWithProjections.nodes.forEach((node) => {
      const nodeId = getNodeId({
        id: node.group?.id ?? node.id,
        afterDeath: node.afterDeath,
      });

      waterfallIds.add(nodeId);
    });

    setInitWaterfallNodeIds(waterfallIds);
  }, [initWaterfallNodeIds.size, waterfall]);

  return {
    waterfallWithFilter,
    isFilteredWaterfall,
    hasWaterfallData,
    visibleNodeIds,
    hiddenNodeIds,
    initWaterfallNodeIds,
  };
}

export interface useEstateWaterfallDetailsOutput<
  T extends
    | EstateWaterfallFragment
    | ClientPresentationDesignerV2_EstateWaterfallFragment,
> {
  waterfallWithFilter: T | null;
  isFilteredWaterfall: boolean;
  hasWaterfallData: boolean;
  visibleNodeIds: string[];
  hiddenNodeIds: string[];
  initWaterfallNodeIds: Set<string>;
}
